import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Brain, RefreshCw, Sparkles, Upload, FileText, ExternalLink, Loader2, ArrowRight, Crown, Calendar, Newspaper } from 'lucide-react';
import SkillNewsService, { SkillNewsResponse, SkillNewsItem, SkillNewsUsageResponse, SkillNewsAccessCheck } from '../../../services/skillNewsService';
import careerService from '../../../services/careerService';
import { useToast } from '../../../components/common/Toast';
import { ParticleBackground } from '../../../components/ParticleBackground';
import { withPageTransition, PageTransitionItem } from '../../../utils/withTransition';
import { Button } from '../../../components/common/Button';
import { FeedSkeletonLoader } from '../../../components/ui/SkeletonLoader';
import SkillNewsCard from '../../../components/ui/SkillNewsCard';
import SkillNewsUsageCard from '../../../components/ui/SkillNewsUsageCard';
import professionalFeedsService, { ProfessionalArticle, ProfessionalFeedsResponse } from '../../../services/professionalFeedsService';
import { useWeekendDetection } from '../../../hooks/useWeekendDetection';

const FeedPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Resume checking state
  const [hasResumes, setHasResumes] = useState<boolean | null>(null);
  const [checkingResumes, setCheckingResumes] = useState(false);
  const { isWeekend } = useWeekendDetection();

  // Skill news state
  const [skillNews, setSkillNews] = useState<SkillNewsItem[]>([]);
  const [filteredSkillNews, setFilteredSkillNews] = useState<SkillNewsItem[]>([]);
  const [skillNewsLoading, setSkillNewsLoading] = useState(false);
  const [skillNewsError, setSkillNewsError] = useState<string | null>(null);
  const [generatingSkillNews, setGeneratingSkillNews] = useState(false);
  const [selectedSkillFilter, setSelectedSkillFilter] = useState<string>('all');

  // Async job states
  const [asyncJobLoading, setAsyncJobLoading] = useState(false);
  const [asyncJobError, setAsyncJobError] = useState<string | null>(null);

  // Usage tracking states
  const [usageData, setUsageData] = useState<SkillNewsUsageResponse | null>(null);
  const [accessCheck, setAccessCheck] = useState<SkillNewsAccessCheck | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);

  // View toggle state
  const [viewMode, setViewMode] = useState<'skill-based' | 'professional'>('skill-based');
  
  // Professional articles state
  const [professionalArticles, setProfessionalArticles] = useState<ProfessionalArticle[]>([]);
  const [professionalCategories, setProfessionalCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [professionalLoading, setProfessionalLoading] = useState(false);
  const [filteredProfessionalArticles, setFilteredProfessionalArticles] = useState<ProfessionalArticle[]>([]);

  // Ref to prevent multiple simultaneous usage data fetches
  const fetchingUsageRef = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  // Fetch professional articles
  const fetchProfessionalArticles = async () => {
    // Not needed for now - Unimplemented from Backend
    return;
    setProfessionalLoading(true);
    try {
      const response = await professionalFeedsService.getProfessionalArticles({ limit: 5 });
      setProfessionalArticles(response.articles);
      setFilteredProfessionalArticles(response.articles);
      setProfessionalCategories(['all', ...response.categories]);
    } catch (error) {
      console.error('Error fetching professional articles:', error);
      showToast('Failed to load professional articles', 'error');
    } finally {
      setProfessionalLoading(false);
    }
  };

  // Check if user has resumes
  const checkUserResumes = async () => {
    setCheckingResumes(true);
    try {
      const userResumes = await careerService.getResumes();
      const hasUserResumes = userResumes && userResumes.length > 0;
      setHasResumes(hasUserResumes);
      return hasUserResumes;
    } catch (error) {
      console.error('Error checking user resumes:', error);
      // On error, assume no resumes to show the upload screen
      setHasResumes(false);
      return false;
    } finally {
      setCheckingResumes(false);
    }
  };

  // Fetch usage data and access check (consolidated into single API call)
  const fetchUsageData = async () => {
    // Prevent multiple simultaneous calls
    if (fetchingUsageRef.current) {
      console.log('fetchUsageData: Already fetching, skipping...');
      return;
    }

    fetchingUsageRef.current = true;
    setLoadingUsage(true);
    try {
      // Use single consolidated API that returns both usage and access data
      const accessData = await SkillNewsService.checkAccess();
      console.log('fetchUsageData: consolidated data', accessData);
      
      setUsageData(accessData.usage || null);
      setAccessCheck(accessData);
    } catch (error) {
      console.error('Error fetching usage data:', error);
      // Set default values if fetch fails
      setUsageData(null);
      setAccessCheck(null);
    } finally {
      setLoadingUsage(false);
      fetchingUsageRef.current = false;
    }
  };

  // Load data when component mounts
  useEffect(() => {
    setLoading(true);

    const timer = setTimeout(async () => {
      // First check if user has resumes
      const userHasResumes = await checkUserResumes();

      if (userHasResumes) {
        // User has resumes, proceed with skill news (which will also fetch usage data)
        await fetchSkillNewsAsync();
        // Also fetch professional articles
        await fetchProfessionalArticles();
      } else {
        // User doesn't have resumes, just stop loading to show resume requirement screen
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  // Listen for resume upload events
  useEffect(() => {
    const handleResumeUploaded = async () => {
      console.log('FeedPage: Resume uploaded event received, checking resumes...');
      // Clear cache to ensure we get fresh data
      careerService.clearResumeCache();
      
      const userHasResumes = await checkUserResumes();
      
      if (userHasResumes && skillNews.length === 0) {
        // If user now has resumes and no skill news loaded, fetch skill news (which will also fetch usage data)
        await fetchSkillNewsAsync();
        // Also fetch professional articles
        await fetchProfessionalArticles();
      }
    };

    window.addEventListener('resume-uploaded', handleResumeUploaded);
    
    return () => {
      window.removeEventListener('resume-uploaded', handleResumeUploaded);
    };
  }, [skillNews.length]);

  // Replace fetchSkillNews with async job-based version
  const fetchSkillNewsAsync = async () => {
    setSkillNewsLoading(true);
    setSkillNewsError(null);
    try {
      // Use existing access check data if available, otherwise fetch fresh data
      let currentAccessCheck = accessCheck;
      if (!currentAccessCheck) {
        currentAccessCheck = await SkillNewsService.checkAccess();
        setAccessCheck(currentAccessCheck);
        setUsageData(currentAccessCheck.usage || null);
      }
      
      if (!currentAccessCheck.canGenerate) {
        // User cannot generate - set appropriate state
        setSkillNewsLoading(false);
        setSkillNewsError(currentAccessCheck.reason || 'Cannot generate skill news');
        return;
      }

      // Start or resume async job
      const { jobId, status, result, error: jobError } = await SkillNewsService.startAsyncNewsJob({
        skillCount: 5,
        newsPerSkill: 10
      });
      
      if (status === 'complete' && result) {
        setSkillNews(result.news);
        setFilteredSkillNews(result.news);
        setSkillNewsLoading(false);
        // Refresh usage data after successful generation to show updated count
        await fetchUsageData();
        return;
      }
      
      if (status === 'error') {
        setSkillNewsError(jobError || 'Failed to start news job');
        setSkillNewsLoading(false);
        return;
      }
      
      // Poll for completion
      const finalResult = await SkillNewsService.pollAsyncNewsJob(jobId);
      setSkillNews(finalResult.news);
      setFilteredSkillNews(finalResult.news);
      
      // Refresh usage data after successful generation to show updated count
      await fetchUsageData();
    } catch (err) {
      setSkillNewsError((err && (err as any).message) || 'Failed to load news');
    } finally {
      setSkillNewsLoading(false);
      setLoading(false);
    }
  };

  const handleRetry = () => {
    if (hasResumes) {
      fetchSkillNewsAsync();
    } else {
      checkUserResumes();
    }
  };

  // Handle search query changes
  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);
    setIsSearching(true);

    setTimeout(() => {
      if (!query.trim()) {
        setFilteredSkillNews(skillNews);
      } else {
        const filtered = skillNews.filter(skillItem =>
          skillItem.skill.toLowerCase().includes(query.toLowerCase()) ||
          skillItem.latest_updates.some(update =>
            update.title.toLowerCase().includes(query.toLowerCase()) ||
            update.content.toLowerCase().includes(query.toLowerCase())
          )
        );
        setFilteredSkillNews(filtered);
      }
      setIsSearching(false);
    }, 300);
  };

  // Async job-based skill news generation handler
  const handleGenerateSkillNewsAsync = async () => {
    setAsyncJobLoading(true);
    setAsyncJobError(null);
    setGeneratingSkillNews(true); // for button loading
    
    try {
      // Use existing access check data if available, otherwise fetch fresh data
      let currentAccessCheck = accessCheck;
      if (!currentAccessCheck) {
        currentAccessCheck = await SkillNewsService.checkAccess();
        setAccessCheck(currentAccessCheck);
        setUsageData(currentAccessCheck.usage || null);
      }
      
      if (!currentAccessCheck.canGenerate) {
        // Don't show error or toast, since this state is already handled in the subscription required screen
        // setAsyncJobError(currentAccessCheck.reason || 'Cannot generate skill news');
        // showToast(currentAccessCheck.reason || 'Cannot generate skill news', 'error');
        setSkillNewsLoading(false);
        setGeneratingSkillNews(false);
        return;
      }

      const { jobId, status, result, error: jobError } = await SkillNewsService.startAsyncNewsJob({
        skillCount: 5,
        newsPerSkill: 10,
        forceRegenerate: true
      });
      
      if (status === 'complete' && result) {
        setSkillNews(result.news);
        setFilteredSkillNews(result.news);
        showToast(`Generated news for ${result.news.length} skills!`, 'success');
        // Refresh usage data after successful generation to show updated count
        await fetchUsageData();
        return;
      }
      
      if (status === 'error') {
        setAsyncJobError(jobError || 'Failed to start news job');
        return;
      }
      
      // Poll for completion
      const finalResult = await SkillNewsService.pollAsyncNewsJob(jobId);
      setSkillNews(finalResult.news);
      setFilteredSkillNews(finalResult.news);
      showToast(`Generated news for ${finalResult.news.length} skills!`, 'success');
      
      // Refresh usage data after successful generation to show updated count
      await fetchUsageData();
    } catch (err: any) {
      setAsyncJobError(err?.message || 'Failed to load news');
      showToast('Failed to generate skill news', 'error');
    } finally {
      setAsyncJobLoading(false);
      setGeneratingSkillNews(false);
    }
  };

  const handleClearSkillNewsCache = async () => {
    try {
      await SkillNewsService.clearCache();
      showToast('Cache cleared successfully', 'success');
      await fetchSkillNewsAsync();
    } catch (error) {
      showToast('Failed to clear cache', 'error');
      console.error('Error clearing cache:', error);
    }
  };

  const handleSkillFilter = (skill: string) => {
    setSelectedSkillFilter(skill);
    if (skill === 'all') {
      setFilteredSkillNews(skillNews);
    } else {
      setFilteredSkillNews(skillNews.filter(item =>
        item.skill.toLowerCase().includes(skill.toLowerCase())
      ));
    }
  };

  const handleCategoryFilter = (category: string) => {
    setSelectedCategory(category);
    if (category === 'all') {
      setFilteredProfessionalArticles(professionalArticles);
    } else {
      setFilteredProfessionalArticles(professionalArticles.filter(article => 
        article.category.toLowerCase() === category.toLowerCase()
      ));
    }
  };

  const handleUploadResume = () => {
    // Open JobProt dashboard in new tab
    navigate('/settings-preferences?tab=preferences');
  };

  const handleSubscribe = () => {
    // Navigate to subscription page
    navigate('/subscriptions');
  };

  // Resume Required Screen Component
  const ResumeRequiredScreen = () => (
    <PageTransitionItem index={3}>
      <div className="text-center py-12 md:py-16 bg-surface-light/50 dark:bg-surface-dark/50 backdrop-blur-sm border border-border-light dark:border-border-dark rounded-xl">
        <div className="flex flex-col px-4 items-center gap-6 max-w-lg mx-auto">
          {/* Icon */}
          <div className="relative">
            <div className="w-20 h-20 bg-primary-light/10 dark:bg-primary-dark/10 rounded-full flex items-center justify-center">
              <FileText className="h-10 w-10 text-primary-light dark:text-primary-dark" />
            </div>
            <div className="absolute -top-1 -right-1 w-8 h-8 bg-warning-light/20 dark:bg-warning-dark/20 rounded-full flex items-center justify-center">
              <Upload className="h-4 w-4 text-warning-light dark:text-warning-dark" />
            </div>
          </div>

          {/* Heading */}
          <div>
            <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
              Resume Required for Personalized Feed
            </h3>
            <p className="text-text-secondary-light dark:text-text-secondary-dark text-base leading-relaxed">
              To craft your personalized skill-based news feed, we need to analyze your professional background and skills from your resume.
            </p>
          </div>

          {/* Benefits */}
          <div className="bg-surface-light/50 dark:bg-surface-dark/50 rounded-lg p-4 text-left w-full">
            <h4 className="text-sm font-semibold text-text-primary-light dark:text-text-primary-dark mb-3">
              With your resume, you'll get:
            </h4>
            <ul className="space-y-2 text-sm text-text-secondary-light dark:text-text-secondary-dark">
              <li className="flex items-start gap-2">
                <Sparkles className="h-4 w-4 text-primary-light dark:text-primary-dark mt-0.5 flex-shrink-0" />
                <span>News tailored to your specific skills and technologies</span>
              </li>
              <li className="flex items-start gap-2">
                <Brain className="h-4 w-4 text-primary-light dark:text-primary-dark mt-0.5 flex-shrink-0" />
                <span>Industry updates relevant to your experience level</span>
              </li>
              <li className="flex items-start gap-2">
                <RefreshCw className="h-4 w-4 text-primary-light dark:text-primary-dark mt-0.5 flex-shrink-0" />
                <span>Fresh content updated based on your professional growth</span>
              </li>
            </ul>
          </div>

          {/* Call to Action */}
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <Button
              onClick={handleUploadResume}
              className="flex-1"
              variant="primary"
              rightIcon={<ArrowRight size={16} />}
            >
              Upload Resume Now
            </Button>
            <Button
              onClick={() => navigate(-1)}
              className="flex-1"
              variant="outline"
            >
              Read Later
            </Button>
          </div>

          {/* Additional Info */}
          <p className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark">
            Your resume data is processed securely and only used to personalize your experience.
          </p>
        </div>
      </div>
    </PageTransitionItem>
  );

  // Subscription Required Screen Component
  const SubscriptionRequiredScreen = () => (
    <PageTransitionItem index={3}>
      <div className="text-center py-12 md:py-16 bg-surface-light/50 dark:bg-surface-dark/50 backdrop-blur-sm border border-border-light dark:border-border-dark rounded-xl">
        <div className="flex flex-col px-4 items-center gap-6 max-w-lg mx-auto">
          {/* Icon */}
          <div className="relative">
            <div className="w-20 h-20 bg-primary-light/10 dark:bg-primary-dark/10 rounded-full flex items-center justify-center">
              <Crown className="h-10 w-10 text-primary-light dark:text-primary-dark" />
            </div>
            <div className="absolute -top-1 -right-1 w-8 h-8 bg-warning-light/20 dark:bg-warning-dark/20 rounded-full flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-warning-light dark:text-warning-dark" />
            </div>
          </div>

          {/* Heading */}
          <div>
            <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
              Monthly Limit Reached
            </h3>
            <p className="text-text-secondary-light dark:text-text-secondary-dark text-base leading-relaxed">
              You've used all <span className="text-text-primary-light dark:text-text-primary-dark font-medium">{usageData?.monthlyLimit || 5}</span> skill news generations for this month. 
              <br />
              Subscribe to get <span className="text-text-primary-light dark:text-text-primary-dark font-medium">unlimited access</span>.
            </p>
          </div>

          {/* Benefits */}
          <div className="bg-surface-light/50 dark:bg-surface-dark/50 rounded-lg p-4 text-left w-full">
            <h4 className="text-sm font-semibold text-text-primary-light dark:text-text-primary-dark mb-3">
              With Premium, you'll get:
            </h4>
            <ul className="space-y-2 text-sm text-text-secondary-light dark:text-text-secondary-dark">

              <li className="flex items-start gap-2">
                <Brain className="h-4 w-4 text-primary-light dark:text-primary-dark mt-0.5 flex-shrink-0" />
                <span>Specially personalized & closely relevant updates</span>
              </li>
              <li className="flex items-start gap-2">
                <RefreshCw className="h-4 w-4 text-primary-light dark:text-primary-dark mt-0.5 flex-shrink-0" />
                <span>Latest & fresh updates about your skills</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="h-4 w-4 text-primary-light dark:text-primary-dark mt-0.5 flex-shrink-0" />
                <span>Daily skill news with no limits</span>
              </li>
            </ul>
          </div>

          {/* Call to Action */}
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <Button
              onClick={handleSubscribe}
              className="flex-1"
              variant="primary"
              leftIcon={<Crown size={16} />}
            >
              Subscribe Now
            </Button>
            <Button
              onClick={() => navigate(-1)}
              className="flex-1"
              variant="outline"
            >
              Maybe Later
            </Button>
          </div>

          {/* Additional Info */}
          <p className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark">
            Limit resets on <span className="text-text-primary-light dark:text-text-primary-dark font-medium">{new Date(usageData?.year || 0, (usageData?.month || 0) - 1).toLocaleDateString('en-US', { 
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}</span> or subscribe to get unlimited access.
          </p>
        </div>
      </div>
    </PageTransitionItem>
  );

  // Custom background gradient styles
  const backgroundGradient = {
    background: 'radial-gradient(circle at 10% 20%, rgba(90, 92, 106, 0.05) 0%, rgba(32, 45, 58, 0.01) 81.3%)',
    position: 'relative' as 'relative',
    zIndex: 0,
    overflow: 'hidden' as 'hidden'
  };

  // Show loading state while checking resumes or loading skill news
  if (loading || checkingResumes) {
    return (
      <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark" style={backgroundGradient}>
        <ParticleBackground />
        <main className="container mx-auto px-4 pt-24 pb-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-start mb-6 mt-8 hover:cursor-pointer" onClick={() => navigate(-1)}>
              <div className="flex items-center gap-2">
                <ArrowLeft size={14} className="text-text-secondary-light dark:text-text-secondary-dark" />
                <span className="text-text-secondary-light dark:text-text-secondary-dark font-medium text-base">Back to Dashboard</span>
              </div>
            </div>
            <PageTransitionItem index={1}>
              <div className="flex items-center mb-8">
                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-2 flex items-center gap-3">
                    Skill-Based News
                  </h1>
                  <p className="text-text-secondary-light dark:text-text-secondary-dark text-base max-w-3xl font-medium">
                    {checkingResumes ? 'Checking your profile...' : 'Thoughtfully curated news updates based on your professional skills'}
                  </p>
                </div>
              </div>
            </PageTransitionItem>
            <PageTransitionItem index={3}>
              {/* <FeedSkeletonLoader count={5} /> */}
              <div className="flex flex-col items-center text-center justify-center py-16">
                <Loader2 className="h-10 w-10 text-primary-light dark:text-primary-dark animate-spin" />
                <p className="text-text-primary-light dark:text-text-primary-dark text-lg mt-4 font-semibold font-heading">
                  Cooking up today's Skill News for you...
                </p>
                <p className="text-center text-sm text-text-secondary-light dark:text-text-secondary-dark mt-2">
                  This may take <span className="text-text-primary-light dark:text-text-primary-dark font-medium">1-2 minutes</span> to load, but will happen only once, we're working on it...
                </p>
              </div>
            </PageTransitionItem>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark" style={backgroundGradient}>
      {/* Animated gradient elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[10%] left-[5%] w-[40%] h-[30%] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-[80px] animate-pulse-slow"></div>
        <div className="absolute bottom-[20%] right-[10%] w-[25%] h-[40%] rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-[40%] right-[30%] w-[20%] h-[20%] rounded-full bg-primary-light/3 dark:bg-primary-dark/3 blur-[80px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>

      <ParticleBackground />

      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-start mb-6 mt-8 hover:cursor-pointer" onClick={() => navigate(-1)}>
            <div className="flex items-center gap-2">
              <ArrowLeft size={14} className="text-text-secondary-light dark:text-text-secondary-dark" />
              <span className="text-text-secondary-light dark:text-text-secondary-dark font-medium text-base">Back to Dashboard</span>
            </div>
          </div>
          <PageTransitionItem index={1}>
            <div className="flex items-center mb-4">
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-2 flex items-center gap-3">
                  Skill-based News
                </h1>
                <p className="text-text-secondary-light dark:text-text-secondary-dark text-base max-w-3xl font-medium">
                 Thoughtfully curated news updates based on your professional skills
                </p>
              </div>
            </div>
          </PageTransitionItem>

          {/* View Toggle - Not Required for now*/}
          {/* {hasResumes && (
            <PageTransitionItem index={2}>
              <div className="flex justify-center mb-6">
                <div className="inline-flex bg-surface-light dark:bg-surface-dark rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('skill-based')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${viewMode === 'skill-based'
                      ? 'bg-primary-light dark:bg-primary-dark text-white'
                      : 'text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Brain size={16} />
                      Skill News
                    </div>
                  </button>
                  <button
                    onClick={() => setViewMode('professional')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${viewMode === 'professional'
                      ? 'bg-primary-light dark:bg-primary-dark text-white'
                      : 'text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Newspaper size={16} />
                      Professional Articles
                    </div>
                  </button>
                </div>
              </div>
            </PageTransitionItem>
          )} */}

          {/* Usage Card for Non-Premium Users, Shows when can generate more news - Below Header */}
          {/* Also hide when it's weekend */}
          {!isWeekend && hasResumes && usageData && !usageData.isPremium && accessCheck && !accessCheck.isPremium && accessCheck.canGenerate && (
            <PageTransitionItem index={2}>
              <div className="mb-6 mt-6 mx-auto">
                <SkillNewsUsageCard 
                  usage={usageData}
                  onUpgrade={handleSubscribe}
                  compact={false}
                />
              </div>
            </PageTransitionItem>
          )}

          {/* Search field */}
          {/* <PageTransitionItem index={2}>
            <div className="mb-6">
              {!loading && (
              <div className="flex items-center rounded-lg border border-border-light dark:border-border-dark px-3 py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all">
                <Search size={18} className="text-text-tertiary-light dark:text-text-tertiary-dark" />
                <input 
                  type="text" 
                  placeholder="Search skill news by technology or keyword..."
                  className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm"
                  value={searchQuery}
                  onChange={(e) => handleSearchQueryChange(e.target.value)}
                />
                {isSearching && (
                  <div className="animate-spin w-4 h-4 border-2 border-primary-light border-t-transparent rounded-full"></div>
                )}
              </div>
              )}
            </div>
          </PageTransitionItem> */}

          {/* Resume requirement check - show if user has no resumes */}
          {hasResumes === false ? (
            <ResumeRequiredScreen />
          ) : accessCheck && !accessCheck.canGenerate && !accessCheck.isPremium && skillNews.length === 0 ? (
            <SubscriptionRequiredScreen />
          ) : viewMode === 'skill-based' && (loading || skillNewsLoading) && skillNews.length === 0 ? (
            <PageTransitionItem index={3}>
              {/* <FeedSkeletonLoader count={5} /> */}
              <div className="flex flex-col items-center text-center justify-center py-16">
                <Loader2 className="h-10 w-10 text-primary-light dark:text-primary-dark animate-spin" />
                <p className="text-text-primary-light dark:text-text-primary-dark text-lg mt-4 font-semibold font-heading">
                  Cooking up today's Skill News for you...
                </p>
                <p className="text-center text-sm text-text-secondary-light dark:text-text-secondary-dark mt-2">
                  This may take <span className="text-text-primary-light dark:text-text-primary-dark font-medium">1-2 minutes</span> to load, but will happen only once, we're working on it...
                </p>
              </div>
            </PageTransitionItem>
          ) : viewMode === 'skill-based' && (error || skillNewsError) ? (
            <PageTransitionItem index={3}>
              <div className="text-center py-8 px-4 bg-surface-light/50 dark:bg-surface-dark/50 backdrop-blur-sm border border-border-light dark:border-border-dark rounded-xl">
                <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
                  Error Loading Skill News
                </h3>
                <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6 text-base">{error || skillNewsError}</p>
                <div className="flex justify-center">
                  <Button
                    variant="primary"
                    onClick={handleRetry}
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            </PageTransitionItem>
          ) : viewMode === 'skill-based' && skillNews.length === 0 ? (
            <PageTransitionItem index={3}>
              <div className="p-2 text-center">
                <div className="flex flex-col items-center gap-4">
                  <Brain className="h-12 w-12 text-text-tertiary-light dark:text-text-tertiary-dark" />
                  <div>
                    <h3 className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                      No Skill News Available
                    </h3>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                      We'll generate personalized news based on your skills
                      <br />Generate news to get started.
                    </p>
                    <div className="mt-4 flex justify-center gap-3">
                      <Button
                        onClick={handleGenerateSkillNewsAsync}
                        className="px-4 py-2"
                        variant="primary"
                        disabled={generatingSkillNews || asyncJobLoading || (accessCheck && !accessCheck.canGenerate) || false}
                        loading={generatingSkillNews || asyncJobLoading}
                        leftIcon={generatingSkillNews || asyncJobLoading ? undefined : <Sparkles size={16} />}
                      >
                        {(generatingSkillNews || asyncJobLoading) ? 'Generating...' : 'Generate Skill News'}
                      </Button>
                      {accessCheck && !accessCheck.canGenerate && !accessCheck.isPremium && (
                        <Button
                          onClick={handleSubscribe}
                          className="px-4 py-2"
                          variant="outline"
                          leftIcon={<Crown size={16} />}
                        >
                          Subscribe Now
                        </Button>
                      )}
                    </div>
                    {asyncJobLoading && (
                      <div className="flex flex-col items-center justify-center py-8">
                        <Loader2 className="h-8 w-8 text-primary-light dark:text-primary-dark animate-spin" />
                        <p className="text-text-secondary-light dark:text-text-secondary-dark text-base mt-2">
                          Generating your personalized news, please wait...
                        </p>
                      </div>
                    )}
                    {asyncJobError && (
                      <div className="text-red-500 text-sm mt-2">{asyncJobError}</div>
                    )}
                  </div>
                </div>
              </div>
            </PageTransitionItem>
          ) : viewMode === 'skill-based' ? (
            <PageTransitionItem index={3}>
              <div className="space-y-6">
                {/* Action buttons for skill news */}
                <div className="flex flex-wrap gap-3 justify-center mb-0">
                  {/* <Button
                    onClick={handleGenerateSkillNews}
                    variant="primary"
                    disabled={generatingSkillNews}
                    loading={generatingSkillNews}
                    leftIcon={generatingSkillNews ? undefined : <RefreshCw size={16} />}
                  >
                    {generatingSkillNews ? 'Generating...' : 'Generate New News'}
                  </Button> */}
                  {/* <Button
                    onClick={handleClearSkillNewsCache}
                    variant="outline"
                    leftIcon={<Sparkles size={16} />}
                  >
                    Clear Cache
                  </Button> */}
                </div>

                {/* Skill filter buttons */}
                {/* {skillNews.length > 0 && (
                  <div className="flex flex-wrap gap-2 justify-center mb-6">
                    <button
                      onClick={() => handleSkillFilter('all')}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${selectedSkillFilter === 'all'
                        ? 'bg-primary-light dark:bg-primary-dark text-white'
                        : 'bg-surface-light dark:bg-surface-dark text-text-secondary-light dark:text-text-secondary-dark hover:bg-primary-light/10 dark:hover:bg-primary-dark/10'
                        }`}
                    >
                      All Skills <span className={`${selectedSkillFilter === 'all' ? 'text-text-primary-dark dark:text-text-primary-dark' : 'text-text-primary-light dark:text-text-primary-dark'}`}>({skillNews.filter(skillItem => skillItem.latest_updates.length > 0).length})</span>
                    </button>
                    {skillNews.filter(skillItem => skillItem.latest_updates.length > 0).map((skillItem) => (
                      <button
                        key={skillItem.skill}
                        onClick={() => handleSkillFilter(skillItem.skill)}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${selectedSkillFilter === skillItem.skill
                          ? 'bg-primary-light dark:bg-primary-dark text-white'
                          : 'bg-surface-light dark:bg-surface-dark text-text-secondary-light dark:text-text-secondary-dark hover:bg-primary-light/10 dark:hover:bg-primary-dark/10'
                          }`}
                      >
                        {skillItem.skill.length > 38 ? skillItem.skill.slice(0, 38) + '...' : skillItem.skill} <span className={`${selectedSkillFilter === skillItem.skill ? 'text-text-primary-dark dark:text-text-primary-dark' : 'text-text-primary-light dark:text-text-primary-dark'}`}>({skillItem.latest_updates.length})</span>
                      </button>
                    ))}
                  </div>
                )} */}

                {/* Skill news cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {filteredSkillNews.filter(skillNewsItem => skillNewsItem.latest_updates.length > 0).map((skillNewsItem, index) => (
                    <SkillNewsCard
                      key={skillNewsItem.skill}
                      skillNews={skillNewsItem}
                      index={index}
                    />
                  ))}
                </div>
              </div>
            </PageTransitionItem>
          ) : viewMode === 'professional' ? (
            <PageTransitionItem index={3}>
              <div className="space-y-6">
                {/* Category Filter */}
                {professionalCategories.length > 0 && (
                  <div className="flex flex-wrap gap-2 justify-center mb-6">
                    {professionalCategories.map((category) => (
                      <button
                        key={category}
                        onClick={() => handleCategoryFilter(category)}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${selectedCategory === category
                          ? 'bg-primary-light dark:bg-primary-dark text-white'
                          : 'bg-surface-light dark:bg-surface-dark text-text-secondary-light dark:text-text-secondary-dark hover:bg-primary-light/10 dark:hover:bg-primary-dark/10'
                        }`}
                      >
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </button>
                    ))}
                  </div>
                )}

                {/* Professional Articles */}
                {professionalLoading ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <Loader2 className="h-10 w-10 text-primary-light dark:text-primary-dark animate-spin" />
                    <p className="text-text-primary-light dark:text-text-primary-dark text-lg mt-4">
                      Loading professional articles...
                    </p>
                  </div>
                ) : filteredProfessionalArticles.length === 0 ? (
                  <div className="text-center py-8 px-4 bg-surface-light/50 dark:bg-surface-dark/50 backdrop-blur-sm border border-border-light dark:border-border-dark rounded-xl">
                    <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
                      No Articles Found
                    </h3>
                    <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6 text-base">
                      {selectedCategory !== 'all' ? `No articles found for category: ${selectedCategory}` : 'No professional articles available at the moment'}
                    </p>
                    <div className='flex justify-center'>
                    <Button
                      variant="primary"
                      onClick={fetchProfessionalArticles}
                      leftIcon={<RefreshCw size={16} />}
                    >
                      Refresh Articles
                    </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredProfessionalArticles.map((article, index) => (
                      <div
                        key={`${article.source}-${index}`}
                        className="bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark hover:shadow-lg transition-shadow p-6"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-xs font-medium text-primary-light dark:text-primary-dark bg-primary-light/10 dark:bg-primary-dark/10 px-2 py-1 rounded-full">
                            {article.category}
                          </span>
                          <span className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark">
                            {article.source}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-2 line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm mb-4 line-clamp-3">
                          {article.content}
                        </p>
                        {article.topics && article.topics.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-4">
                            {article.topics.slice(0, 3).map((topic, idx) => (
                              <span
                                key={idx}
                                className="text-xs text-text-secondary-light dark:text-text-secondary-dark bg-surface-light dark:bg-surface-dark px-2 py-1 rounded"
                              >
                                {topic}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          {article.published_date && (
                            <span className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark flex items-center gap-1">
                              <Calendar size={12} />
                              {new Date(article.published_date).toLocaleDateString()}
                            </span>
                          )}
                          <a
                            href={article.article_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-light dark:text-primary-dark hover:underline text-sm font-medium flex items-center gap-1"
                          >
                            Read More
                            <ExternalLink size={14} />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </PageTransitionItem>
          ) : null}
        </div>
      </main>

      <style>
        {`
        @keyframes slideInUp {
          0% {
            opacity: 0;
            transform: translateY(60px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-pulse-slow {
          animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 1;
          }
        }
        `}
      </style>
    </div>
  );
};

export default withPageTransition(FeedPage);
